/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useMemo } from 'react';
import { X, Upload, AlertCircle, CheckCircle, Loader, ChevronDown, ChevronRight, Info } from 'lucide-react';
import type { UserStory, Epic } from '../../types';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';

interface LinearUserStoryExportDialogProps {
  epics: Epic[];
  stories: UserStory[];
  onClose: () => void;
  onExportComplete: () => void;
}

type ExportResult = {
  successful: number;
  created: number;
  updated: number;
  failed: number;
  errors: string[];
};

/**
 * Dialog for exporting user stories and epics to Linear.
 * 
 * Velocity Epics are exported as Linear Projects, and User Stories as Linear Issues.
 */
export function LinearUserStoryExportDialog({ epics, stories, onClose, onExportComplete }: LinearUserStoryExportDialogProps) {
  const { linearConfig, isLinearConfigured } = useApp();
  
  const [step, setStep] = useState<'select' | 'options' | 'exporting' | 'complete'>('select');
  const [selectedEpics, setSelectedEpics] = useState<Set<string>>(new Set());
  const [selectedStories, setSelectedStories] = useState<Set<string>>(new Set());
  const [expandedEpics, setExpandedEpics] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skillShopService] = useState(() => new SkillShopService());

  // Export options
  const [targetTeamId, setTargetTeamId] = useState<string>(linearConfig?.defaultTeamId || '');
  const [createProjects, setCreateProjects] = useState(true);
  const [teams, setTeams] = useState<Array<{ id: string; name: string; key: string }>>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // Group stories by epic
  const storiesByEpic = useMemo(() => {
    const grouped: Record<string, UserStory[]> = {};
    stories.forEach(story => {
      const epicId = story.epicId || '__no_epic__';
      if (!grouped[epicId]) {
        grouped[epicId] = [];
      }
      grouped[epicId].push(story);
    });
    return grouped;
  }, [stories]);

  const toggleEpicSelection = (epicId: string) => {
    const newSelected = new Set(selectedEpics);
    const epicStories = storiesByEpic[epicId] || [];
    
    if (newSelected.has(epicId)) {
      newSelected.delete(epicId);
      epicStories.forEach(s => selectedStories.delete(s.id));
      setSelectedStories(new Set(selectedStories));
    } else {
      newSelected.add(epicId);
      const newStorySelection = new Set(selectedStories);
      epicStories.forEach(s => newStorySelection.add(s.id));
      setSelectedStories(newStorySelection);
    }
    setSelectedEpics(newSelected);
  };

  const toggleStorySelection = (storyId: string) => {
    const newSelected = new Set(selectedStories);
    if (newSelected.has(storyId)) {
      newSelected.delete(storyId);
    } else {
      newSelected.add(storyId);
    }
    setSelectedStories(newSelected);
  };

  const toggleEpicExpansion = (epicId: string) => {
    const newExpanded = new Set(expandedEpics);
    if (newExpanded.has(epicId)) {
      newExpanded.delete(epicId);
    } else {
      newExpanded.add(epicId);
    }
    setExpandedEpics(newExpanded);
  };

  const selectAll = () => {
    setSelectedEpics(new Set(epics.map(e => e.id)));
    setSelectedStories(new Set(stories.map(s => s.id)));
  };

  const selectNone = () => {
    setSelectedEpics(new Set());
    setSelectedStories(new Set());
  };

  const selectedEpicsList = epics.filter(e => selectedEpics.has(e.id));
  const selectedStoriesList = stories.filter(s => selectedStories.has(s.id));
  const totalSelected = selectedEpics.size + selectedStories.size;

  const loadTeams = async () => {
    if (!linearConfig?.apiKey) return;
    
    setLoadingTeams(true);
    try {
      const response = await skillShopService.getLinearTeams({
        apiKey: linearConfig.apiKey,
        defaultTeamId: linearConfig.defaultTeamId || undefined
      });
      if (response.success && response.data) {
        setTeams(response.data.teams || []);
      }
    } catch (err: any) {
      console.error('Failed to load Linear teams:', err);
    } finally {
      setLoadingTeams(false);
    }
  };

  const goToOptions = async () => {
    if (totalSelected === 0) {
      setError('Please select at least one epic or story to export.');
      return;
    }
    
    // Load teams when entering options step
    if (teams.length === 0 && linearConfig?.apiKey) {
      await loadTeams();
    }
    
    setStep('options');
  };

  const performExport = async () => {
    if (!linearConfig?.apiKey) {
      setError('Linear API key is not configured.');
      return;
    }

    if (!targetTeamId) {
      setError('Please select a target team.');
      return;
    }

    setExporting(true);
    setError(null);
    setStep('exporting');

    // Format data for export
    const epicsToExport = selectedEpicsList.map(epic => ({
      id: epic.id,
      title: epic.title,
      description: epic.description,
      status: epic.status
    }));

    const storiesToExport = selectedStoriesList.map(story => ({
      id: story.id,
      title: story.title,
      role: story.role,
      goal: story.goal,
      benefit: story.benefit,
      acceptanceCriteria: story.acceptanceCriteria || [],
      epicId: story.epicId,
      status: story.status
    }));

    const result = await skillShopService.exportUserStoriesToLinear({
      apiKey: linearConfig.apiKey,
      defaultTeamId: targetTeamId,
    }, epicsToExport, storiesToExport, {
      teamId: targetTeamId,
      createProjects,
    });

    if (result.success && result.data) {
      setExportResult(result.data);
      setStep('complete');
    } else {
      setError(result.error || 'Export failed');
      setStep('options');
    }
    
    setExporting(false);
  };

  const handleComplete = () => {
    onExportComplete();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Export User Stories to Linear
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info banner about export mapping */}
        <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Export Mapping</p>
            <p className="text-sm text-blue-700 mt-1">
              Epics will be created as Linear Projects, and Stories as Linear Issues.
              Stories will include the user story format in the issue description.
            </p>
          </div>
        </div>

        {!isLinearConfigured && (
          <div className="mx-6 mt-4 p-4 rounded-lg bg-yellow-50 border border-yellow-200 text-yellow-900 text-sm">
            Configure the Linear integration first by adding an API key in the Settings page.
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Error</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Selection Step */}
        {step === 'select' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Select Items to Export</h3>
                <p className="text-sm text-gray-600">
                  {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={selectAll}
                  className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Select All
                </button>
                <button
                  onClick={selectNone}
                  className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  Select None
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              {epics.length === 0 && stories.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  No user stories or epics to export
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {epics.map(epic => {
                    const epicStories = storiesByEpic[epic.id] || [];
                    const isExpanded = expandedEpics.has(epic.id);
                    
                    return (
                      <div key={epic.id}>
                        <div className="flex items-center gap-3 p-3 hover:bg-gray-50">
                          <button
                            onClick={() => toggleEpicExpansion(epic.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <input
                            type="checkbox"
                            checked={selectedEpics.has(epic.id)}
                            onChange={() => toggleEpicSelection(epic.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <span className="font-medium text-gray-900">{epic.id}</span>
                            <span className="text-gray-600 ml-2">{epic.title}</span>
                          </div>
                          <span className="text-xs text-gray-500 bg-purple-100 text-purple-700 px-2 py-1 rounded">
                            → Project
                          </span>
                        </div>
                        
                        {isExpanded && epicStories.length > 0 && (
                          <div className="pl-12 bg-gray-50 border-t border-gray-100">
                            {epicStories.map(story => (
                              <div key={story.id} className="flex items-center gap-3 p-2 hover:bg-gray-100">
                                <input
                                  type="checkbox"
                                  checked={selectedStories.has(story.id)}
                                  onChange={() => toggleStorySelection(story.id)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <span className="text-sm font-medium text-gray-900">{story.id}</span>
                                  <span className="text-sm text-gray-600 ml-2">{story.title}</span>
                                </div>
                                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                                  → Issue
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Stories without epic */}
                  {storiesByEpic['__no_epic__']?.map(story => (
                    <div key={story.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                      <div className="w-4" />
                      <input
                        type="checkbox"
                        checked={selectedStories.has(story.id)}
                        onChange={() => toggleStorySelection(story.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-900">{story.id}</span>
                        <span className="text-gray-600 ml-2">{story.title}</span>
                      </div>
                      <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                        → Issue
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={goToOptions}
                disabled={totalSelected === 0 || !isLinearConfigured}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue ({totalSelected} selected)
              </button>
            </div>
          </div>
        )}

        {/* Options Step */}
        {step === 'options' && (
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Export Options</h3>
              <p className="text-sm text-gray-600">
                Configure how items will be created in Linear.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Team</label>
                {loadingTeams ? (
                  <div className="flex items-center gap-2 text-gray-600 text-sm py-2">
                    <Loader className="w-4 h-4 animate-spin" />
                    Loading teams...
                  </div>
                ) : teams.length > 0 ? (
                  <select
                    value={targetTeamId}
                    onChange={(e) => setTargetTeamId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a team...</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.key})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-gray-600 py-2">No teams available.</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="createProjects"
                  checked={createProjects}
                  onChange={(e) => setCreateProjects(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="createProjects" className="text-sm text-gray-700">
                  Create Linear Projects for Epics
                </label>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Export Summary</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• {selectedEpics.size} epic{selectedEpics.size !== 1 ? 's' : ''} → Linear Projects</li>
                  <li>• {selectedStories.size} stor{selectedStories.size !== 1 ? 'ies' : 'y'} → Linear Issues</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setStep('select')}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={performExport}
                disabled={exporting || !targetTeamId}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {exporting && <Loader className="w-4 h-4 animate-spin" />}
                Export to Linear
              </button>
            </div>
          </div>
        )}

        {/* Exporting Step */}
        {step === 'exporting' && (
          <div className="p-6 text-center">
            <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-900">Exporting to Linear...</p>
            <p className="text-sm text-gray-600 mt-2">Creating projects and issues</p>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && exportResult && (
          <div className="p-6">
            <div className="text-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Export Complete!</h3>
              <p className="text-gray-600">
                Successfully exported {exportResult.successful} items to Linear
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{exportResult.created || 0}</p>
                <p className="text-sm text-green-900">Created</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{exportResult.updated || 0}</p>
                <p className="text-sm text-blue-900">Updated</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{exportResult.failed || 0}</p>
                <p className="text-sm text-red-900">Failed</p>
              </div>
            </div>

            {exportResult.errors?.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Errors:</h4>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {exportResult.errors.map((err, index) => (
                      <li key={index}>• {err}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <button
              onClick={handleComplete}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

